import { createContext, useContext, ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { api, CurrentUser } from "@/lib/api";

interface AuthState {
  /** Set when /api/me succeeds. While loading, null even if a session may exist. */
  user: CurrentUser | null;
  /** True until the first /api/me completes — hide login/Admin until identity is known. */
  isAuthLoading: boolean;
  isLoggedIn: boolean;
  /**
   * True only for host-authorized operators: the server matches your Discord ID
   * against configured owner/admin entries. Stays false until that succeeds.
   */
  isDashboardOwner: boolean;
  refetch: () => void;
}

const AuthContext = createContext<AuthState | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const { data, isSuccess, isPending, refetch } = useQuery({
    queryKey: ["me"],
    queryFn: () => api.me(),
    retry: false,
    staleTime: 0,
    gcTime: 5 * 60 * 1000,
    refetchOnMount: "always",
  });

  const isAuthLoading = isPending;
  const isLoggedIn = isSuccess && data != null;
  const user: CurrentUser | null = isLoggedIn ? (data as CurrentUser) : null;
  const isDashboardOwner = Boolean(user?.is_dashboard_owner);

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthLoading,
        isLoggedIn,
        isDashboardOwner,
        refetch,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be inside AuthProvider");
  return ctx;
}
